// src/redux/features/commentsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    collection,
    addDoc,
    deleteDoc,
    updateDoc,
    doc,
    getDocs,
    query,
    where,
    // orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Async thunk to fetch comments for a specific blog
export const fetchComments = createAsyncThunk(
    'comments/fetchComments',
    // eslint-disable-next-line no-unused-vars
    async (blogId) => {
        const commentsRef = collection(db, 'comments');
        const q = query(
            commentsRef,
            where('blogId', '==', blogId)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    }
);

// Async thunk to create a new comment
export const createComment = createAsyncThunk(
    'comments/createComment',
    async ({ blogId, content, userId, userName }) => {
        const commentsRef = collection(db, 'comments');
        const commentData = {
            blogId,
            content,
            userId,
            userName,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(commentsRef, commentData);
        return {
            id: docRef.id,
            ...commentData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
    }
);

// Async thunk to update a comment
export const updateComment = createAsyncThunk(
    'comments/updateComment',
    async ({ commentId, content }) => {
        const commentRef = doc(db, 'comments', commentId);
        const updateData = {
            content,
            updatedAt: serverTimestamp()
        };
        await updateDoc(commentRef, updateData);
        return { id: commentId, updates: updateData };
    }
);

// Async thunk to delete a comment
export const deleteComment = createAsyncThunk(
    'comments/deleteComment',
    async (commentId) => {
        await deleteDoc(doc(db, 'comments', commentId));
        return commentId;
    }
);

const commentsSlice = createSlice({
    name: 'comments',
    initialState: {
        items: [],
        status: 'idle',
        error: null
    },
    reducers: {
        clearComments: (state) => {
            state.items = [];
            state.status = 'idle';
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch comments
            .addCase(fetchComments.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchComments.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
                state.error = null;
            })
            .addCase(fetchComments.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Create comment
            .addCase(createComment.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(createComment.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items.unshift(action.payload);
            })
            .addCase(createComment.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Update comment
            .addCase(updateComment.fulfilled, (state, action) => {
                const { id, updates } = action.payload;
                const comment = state.items.find(comment => comment.id === id);
                if (comment) {
                    Object.assign(comment, updates);
                }
            })
            // Delete comment
            .addCase(deleteComment.fulfilled, (state, action) => {
                state.items = state.items.filter(comment => comment.id !== action.payload);
            });
    }
});

export const { clearComments } = commentsSlice.actions;
export default commentsSlice.reducer;